import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { populateDefaults } from "@/utils/demo-data";

export function useDemoData() {
  const { user } = useAuth();
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;

    const checkDemoMode = async () => {
      const { data } = await supabase
        .from("user_preferences")
        .select("has_demo_data")
        .eq("user_id", user.id)
        .maybeSingle();

      setIsDemoMode((data as any)?.has_demo_data === true);
    };

    checkDemoMode();
  }, [user]);

  const clearDemoData = async () => {
    if (!user) return false;
    setIsClearing(true);

    try {
      // Delete demo data in order (respecting FK constraints)
      // 1. contract_notes
      await (supabase
        .from("contract_notes")
        .delete() as any)
        .eq("user_id", user.id)
        .eq("is_demo_data", true);

      // 2. contract_contacts for demo contracts
      const { data: demoContracts } = await (supabase
        .from("contracts")
        .select("id") as any)
        .eq("user_id", user.id)
        .eq("is_demo_data", true);

      if (demoContracts && demoContracts.length > 0) {
        const contractIds = demoContracts.map((c: any) => c.id);
        await supabase
          .from("contract_contacts")
          .delete()
          .in("contract_id", contractIds);
      }

      // 3. contracts
      await (supabase
        .from("contracts")
        .delete() as any)
        .eq("user_id", user.id)
        .eq("is_demo_data", true);

      // 4. contact_persons
      await (supabase
        .from("contact_persons")
        .delete() as any)
        .eq("user_id", user.id)
        .eq("is_demo_data", true);

      // 5. suppliers
      await (supabase
        .from("suppliers")
        .delete() as any)
        .eq("user_id", user.id)
        .eq("is_demo_data", true);

      // Update preferences
      await supabase
        .from("user_preferences")
        .update({ has_demo_data: false } as any)
        .eq("user_id", user.id);

      // Re-populate clean defaults
      await populateDefaults(user.id);

      // Invalidate all caches
      await queryClient.invalidateQueries();

      setIsDemoMode(false);
      navigate("/contracts");
      return true;
    } catch (error) {
      console.error("Error clearing demo data:", error);
      return false;
    } finally {
      setIsClearing(false);
    }
  };

  return { isDemoMode, isClearing, clearDemoData };
}
